<template>
  <v-select
    v-model="selectedItems"
    :items="filteredItems"
    :label="label"
    item-text="name"
    item-value="id"
    item-color="secondary"
    color="secondary"
    @change="select"
    hide-details
    multiple
    single-line
    outlined
    dense
  >
    <template v-slot:prepend-item v-if="search">
      <v-list-item>
        <v-text-field
          v-model="keyword"
          placeholder="Search"
          hide-details
          :disabled="isLoading"
        />
      </v-list-item>
    </template>
    <template v-slot:selection="{ item, index }">
      <span class="item-name" v-if="index === 0">{{ item.name }}</span>
      <span v-else-if="index === 1" class="font-weight-bold more-item-text">
        + {{ selectedItems.length - 1 }} more
      </span>
    </template>
    <template v-slot:append-item>
      <div
        v-if="isNeedToLoadMore"
        v-intersect="onIntersect"
        class="pa-4 teal--text"
      >
        {{ isLoading ? 'Loading more items...' : 'No data...' }}
      </div>
    </template>
  </v-select>
</template>

<script>
import * as _ from 'lodash'

import { sendGet } from '@/util/api'

export default {
  name: 'MultipleSelectFilter',
  props: {
    label: String,
    filterKey: String,
    selected: Array,
    url: String,
    dataType: {
      type: String,
      default: 'object',
    },
    apiPrimaryKey: {
      type: String,
      default: 'id',
    },
    apiNameKey: {
      type: String,
      default: 'name',
    },
    isImmediately: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isLoading: false,
      selectedItems: [],
      listItems: [],
      preselectedItems: [],
      keyword: '',
      lastKeyword: '',
      offset: 0,
      paginationLimit: 20,
      totalCount: 0,
    }
  },
  computed: {
    filteredItems() {
      const data = [
        { id: '-1', name: 'All ' + this.label },
        ...this.preselectedItems,
        ...this.listItems,
      ]

      const uniqueData = _.uniqBy(data, 'id')
      return uniqueData
    },

    isNeedToLoadMore () {
      if (!this.totalCount) {
        return true
      }

      return this.totalCount !== this.listItems.length
    },

    isDefaultItemSelected() {
      if (this.selectedItems.length === 0 || this.selectedItems.includes('-1')) {
        return true
      }

      return false
    }
  },
  watch: {
    keyword: _.debounce(function(newVal){
      this.fetchListData()
    }, 500),

    selected: {
      handler: function(val) {
        if (!val || val.length == 0) {
          this.selectAllItems()
        } else {
          this.selectedItems = _.cloneDeep(this.selected)
        }
      },
      deep: true
    }
  },
  methods: {
    onIntersect() {
      this.fetchListData()
    },

    async fetchListData() {
      if (this.isLoading) {
        return
      }

      if (this.lastKeyword !== this.keyword) {
        this.offset = 0
        this.lastKeyword = this.keyword
        this.getPreselectedItems()
      }

      try {
        const query = {
          offset: this.offset,
          limit: this.paginationLimit,
          ordering: 'name',
        }

        if (this.keyword) {
          if (this.filterKey === 'deviceModel') {
            query.device_model_contains = this.keyword;
          } else {
            query.name_contains = this.keyword;
          }
        }

        this.isLoading = true
        const response = await sendGet(this.url, query)
        const results = response.results.map(item => {
          if (this.dataType === 'object') {
            return {
              id: item[this.apiPrimaryKey],
              name: item[this.apiNameKey]
            }
          } else {
            return {
              id: item,
              name: item,
            }
          }
        })

        if (this.offset === 0) {
          this.listItems = results
        } else {
          this.listItems = [
            ...this.listItems,
            ...results,
          ]
        }

        this.totalCount = response.count
        this.offset += this.paginationLimit
      } catch (error) {
        console.log(error)
      }
      this.isLoading = false
    },

    select(e) {
      if (e[e.length - 1] == '-1' || e.length == 0) {
        this.selectAllItems();
        this.$emit('select', [], this.filterKey, [], this.isImmediately)
        return
      }

      let result = _.cloneDeep(this.selectedItems)
      const index = this.selectedItems.indexOf('-1')

      if (this.selectedItems.length > 0) {
        if (index > -1) {
          result.splice(index, 1);
        }

        this.$emit('select', result, this.filterKey, this.filteredItems, this.isImmediately)

        if (this.selectedItems.length > 1) {
          this.selectedItems = _.cloneDeep(result)
        }
      } else {
        this.selectAllItems()
      }
    },

    selectAllItems() {
      this.selectedItems = ['-1']
      this.preselectedItems = []
    },

    getPreselectedItems() {
      const preselectedItems = []
      for (const id of this.selectedItems) {
        const preselectedItem = this.filteredItems.find(item => item.id === id)

        if (preselectedItem) {
          preselectedItems.push(preselectedItem)
        }
      }

      this.preselectedItems = preselectedItems
    }
  },
  mounted() {
    if (!this.selected || this.selected.length == 0) {
      this.selectAllItems()
    } else {
      this.selectedItems = _.cloneDeep(this.selected)
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .v-select__selections {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-wrap: nowrap;

  .item-name {
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 5px;
  }
  input {
    width: 10px;
  }
}

::v-deep .v-list-item__action {
  margin-right: 10px!important;
}
</style>
